import React, { useState, useEffect, useContext, useCallback } from 'react';
import PropTypes from 'prop-types';
import ReactPlaceholder from 'react-placeholder';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import LabeledSwitch from "./LabeledSwitch";
import AuthorizedUser from "../utils/AuthorizedUser";
import AuthorizedUserContext from "../utils/AuthorizedUserContext";
import ShareEscalationForm from "./ShareEscalationForm";
import Fido from '../utils/Fido';
import MomentCore from 'moment';
import HTMLEntities from "../utils/HTMLEntities";

import {
	makeStyles,
	Grid,
	Button,
	Typography
} from '@material-ui/core';

import Heading from "./Heading";
import Constants from "../utils/Constants";
import PaddedContent from './PaddedContent';
import EscalationConfirmationDialog from './EscalationConfirmationDialog';

const styles = makeStyles(theme => ({
	signalHeading: {
		fontWeight: 700,
		lineHeight: "20px"
	},
	paddedContent: {
		backgroundColor: '#e9e9e9',
		[theme.breakpoints.down('xs')]: {
			padding: theme.spacing(0.5)
		}
	},
	bottomPad: {
		paddingBottom: theme.spacing(2)
	},

	marginTop: {
		marginTop: theme.spacing(2)
	},

	button: {
		width: "100%"
	},

	icon: {
		fontSize: theme.spacing(2),
		marginLeft: theme.spacing(0.5),
		//marginRight: theme.spacing(2)
	},

	escalateIcon: {
		marginLeft: theme.spacing(0.25),
		//marginRight: theme.spacing(1.75)
	},

	escalateLabel: {
		paddingLeft: theme.spacing(1.75)
	},

	workflowButtonContainer: {
		width: theme.spacing(22),

		[theme.breakpoints.up('lg')]: {
			width: theme.spacing(20)
		}
	},

	buttonLabel: {
		paddingLeft: theme.spacing(2)
	}
}));

function checkOwnedSource(sourceName) {

	switch (sourceName) {
		case Constants.SOURCES.GMAIL:
		case Constants.SOURCES.GOOGLE_HANGOUTS:
		case Constants.SOURCES.GOOGLE_DRIVE:
		case Constants.SOURCES.EXCHANGE:
		case Constants.SOURCES.WEB_SEARCH:
			return true;

		default:
			return false;
	}

}
function WorkflowButton(props) {
	let classes = styles(),
		{ disabled, className, startIcon, onClick, label } = props;

	return (
		<Button
			color="primary"
			variant="contained"
			disabled={disabled}
			className={className}
			onClick={onClick}>
			<div style={{ marginRight: '10px' }}>
				{startIcon}
			</div>
			{label}
		</Button>
	);
}

WorkflowButton.propTypes = {
	disabled: PropTypes.bool,
	className: PropTypes.string,
	startIcon: PropTypes.object,
	onClick: PropTypes.func,
	label: PropTypes.string
};

const ActivityWorkflowActions = React.memo(function ActivityWorkflowActions(props) {
	let classes = styles(),
		paddedRowCls = `${classes.paddedRow} ${classes.rowWidth}`,
		{ activity, status, careStatus, sourceName, isDeleted, activityId, noActionHandler, careStatusHandler, moveStatusHandler, showSnackbar, updateActivityHandler } = props,
		workFlow = {},
		//questionStorageKey = "workflow-question",
		workflowQuestionCount = 0; //localStorage.getItem(questionStorageKey) || 0;

	const authenticatedUser = useContext(AuthorizedUserContext);
	const [shareOpen, setShareOpen] = useState(false);
	const [notesObj, setNotesObj] = useState();
	const [escalationConfirmationOpen, setEscalationConfirmationOpen] = useState(false);

	const [shareMenuOpen, setShareMenuOpen] = useState(false);
	const share = (e) => {
		setShareMenuOpen(e.currentTarget);
	};

	const handleShareMenuClose = (activityId, secondId, secondMsg) => {
		setShareMenuOpen(false);
	};

	const onShareItemClicked = (activityId, secondId, secondMsg) => {
		if (secondId === Constants.ACTIVITY.STATUSES.ESCALATED.ID) {
			setEscalationConfirmationOpen(true);
			return;
		}
		moveStatusHandler(activityId, secondId, secondMsg);
	};

	const handleEscalationConfirmationClose = () => {
		setEscalationConfirmationOpen(false);
	};

	const handleEscalationConfirmationSubmit = async () => {
		moveStatusHandler(activityId, workFlow[status].secondId, workFlow[status].secondMsg);
		if (activity?.auditTrail &&
			activity?.auditTrail.findIndex(item => item.type === Constants.ACTIVITY.AUDIT_TRAIL.REPORT_TO_BTA.ID) === -1) {
			const notes = await buildReport();
			shareToBta(notes);
		}
		handleEscalationConfirmationClose();
	};

	const shareToBta = (notesObj) => {
		if (notesObj !== false && notesObj !== undefined) {
			let fido = new Fido();
			fido.fetch(`/activities/${activityId}/report-to-bta`, {
				method: "POST",
				body: JSON.stringify({ report: notesObj })
			}, 'Sending to Case Manager ...', 'Sent to Case Manager Queue.')
				.then(data => {
					// if (data && data.activity && !isJsonEqual(data.activity, activity)) {
					// updateActivityHandler(data);
					// }
					console.log("Activity reported to BTA successfully", data);
				})
				.catch(error => showSnackbar(error.message));
		}
	};

	const buildReport = useCallback(() => {
		const isValidObject = obj => obj && typeof obj === "object" && Object.keys(obj).length > 0;
		let reportObj = null;

		if (isValidObject(activity)) {
			const obj = {
				incidentDate: MomentCore(activity.published).toISOString(),
				author: activity.author
			};

			const isTwitter = activity.sourceId === Constants.SOURCES.IDS.TWITTER;
			const isGDoc =
				activity.sourceId === Constants.SOURCES.IDS.GOOGLE_DOCS &&
				activity.subSourceId !== Constants.SOURCES.IDS.GSUIT_COMMENT;

			const topicsArray = activity.postMatches?.map(t => t.library_topic?.name || "") || [];
			const uniqueTopicsArray = [...new Set(topicsArray)];

			const category =
				activity.category === Constants.ACTIVITY.CATEGORIES.DISCUSSIONS.ID
					? Constants.ACTIVITY.CATEGORIES.DISCUSSIONS.NAME
					: Constants.ACTIVITY.CATEGORIES.ALERTS.NAME;


			const anonymize = activity && activity.extraHeaders && authenticatedUser.ghosting;

			const authorName = `${activity.authorName} ${activity.extraHeaders && !anonymize
				? activity.extraHeaders.from
				: `(${activity.sourceHandle})`
				}`;

			obj.notes = [
				`<div><strong>Category:</strong> ${category.charAt(0).toUpperCase() + category.slice(1)}</div>`,
				`<div><strong>Source:</strong> ${Constants.SOURCES.getDisplayNameFromId(activity.sourceId)}</div>`,
				`<div><strong>Topic:</strong> ${uniqueTopicsArray.join(", ")}</div>`,
				`<div><strong>Associated Locations:</strong> ${activity.associatedLocations?.map(l => `${l.name} - ${l.address}`).join(", ") || ""
				}</div>`,
				`<div><br/><strong>Author Info:</strong> ${!isTwitter
					? isGDoc
						? activity.authors.split(",").join(", ")
						: authorName
					: `<a href='${activity.authorLink}' target='_blank' class='${classes.link}'>Author Profile</a>`
				}</div>`,
				`<div><strong>Content:</strong> ${isTwitter
					? `<a href='${activity.postLink}' target='_blank' class='${classes.link}'>View on Twitter</a>`
					: HTMLEntities.decode(activity.content)
				}</div>`
			].join("");

			setNotesObj(obj);
			reportObj = obj;
		}
		return reportObj;
	}, [activity, authenticatedUser, classes]);

	const handleShareDialogClose = () => {
		setShareOpen(false);
	};

	const handleShareSubmit = (activityId, values) => {
		handleShareDialogClose();

		let poodle = new Fido();
		poodle.fetch(`/activities/${activityId}/share`, {
			method: "PUT",
			body: JSON.stringify(values)
		}, 'Sharing...', 'Shared.')
			.then(data => {
				// updateActivityHandler(data);
			})
			.catch(error => props.showSnackbar(error.message));
	};

	// define next status in workflow
	workFlow[Constants.ACTIVITY.STATUSES.NEW.ID] = {
		id: Constants.ACTIVITY.STATUSES.FOR_REVIEW.ID,
		nextIcon: ["fas", "eye"],
		nextLabel: "Needs Review",
		msg: "Needs Review...",
		secondId: Constants.ACTIVITY.STATUSES.ESCALATED.ID,
		secondNextIcon: ["fas", "arrow-alt-circle-up"],
		secondNextIconClass: classes.escalateIcon,
		secondNextLabel: "Escalate",
		secondNextLabelClass: classes.escalateLabel,
		secondMsg: "Escalating..."
	};

	workFlow[Constants.ACTIVITY.STATUSES.FOR_REVIEW.ID] = {
		id: Constants.ACTIVITY.STATUSES.ESCALATED.ID,
		label: Constants.ACTIVITY.STATUSES.FOR_REVIEW.NAME,
		nextIcon: ["fas", "arrow-alt-circle-up"],
		nextIconClass: classes.escalateIcon,
		nextLabel: "Escalate",
		msg: "Escalating..."
	};

	workFlow[Constants.ACTIVITY.STATUSES.ESCALATED.ID] = {
		id: Constants.ACTIVITY.STATUSES.RESOLVED.ID,
		label: Constants.ACTIVITY.STATUSES.ESCALATED.NAME,
		nextIcon: ["fas", "arrow-alt-circle-up"],
		nextLabel: "Resolve",
		msg: "Resolving..."
	};

	workFlow[Constants.ACTIVITY.STATUSES.RESOLVED.ID] = {
		id: Constants.ACTIVITY.STATUSES.FOR_REVIEW.ID,
		label: Constants.ACTIVITY.STATUSES.RESOLVED.NAME,
		nextIcon: ["fas", "binoculars"],
		nextLabel: "Needs Review",
		msg: "Needs Review...",
		secondId: Constants.ACTIVITY.STATUSES.ESCALATED.ID,
		secondNextIcon: ["fas", "engine-warning"],
		secondNextIconClass: classes.escalateIcon,
		secondNextLabel: "Escalate",
		secondNextLabelClass: classes.escalateLabel,
		secondMsg: "Escalating..."
	};


	return (
		<ReactPlaceholder
			rows={2}
			type='text'
			ready={activityId != null}
			className={classes.bottomPad}>
			{status && status !== Constants.ACTIVITY.STATUSES.NEW.ID ? (
				<Typography><b>Status:</b> {workFlow[status].label}</Typography>
			) : null}

			{AuthorizedUser.hasCare(authenticatedUser) && checkOwnedSource(sourceName) ? (
				<LabeledSwitch
					id={`activity_care_status_${activityId}`}
					name={`activity_care_status_${activityId}`}
					label={careStatus === Constants.ACTIVITY.CARE_STATUSES.RESOLVED.ID ? "This alert has been resolved in CARE" : "Share this alert with CARE"}
					className={`${paddedRowCls} ${classes.sendBlankToggle}`}
					checked={careStatus === Constants.ACTIVITY.CARE_STATUSES.SHARED.ID}
					disabled={careStatus === Constants.ACTIVITY.CARE_STATUSES.RESOLVED.ID}
					onChange={event => careStatusHandler(event, activityId, careStatus)} />
			) : null}


			<React.Fragment>

				{workflowQuestionCount <= 5 ?
					<Heading
						title="What should be done with this signal?" className={classes.signalHeading} />
					: null
				}
				<Grid
					container
					direction="column"
					id="workflowActionsGrid"
					className={classes.marginTop}>
					<Grid
						item
						className={classes.bottomPad}>
						<WorkflowButton
							disabled={isDeleted}
							className={`${classes.button} ${workFlow[status].nextLabelClass}`}
							label={workFlow[status].nextLabel}
							startIcon={<FontAwesomeIcon className={`${classes.icon} ${workFlow[status].nextIconClass}`} icon={workFlow[status].nextIcon} />}
							onClick={() => moveStatusHandler(activityId, workFlow[status].id, workFlow[status].msg)} />
					</Grid>
					{workFlow[status].secondNextLabel ?
						<Grid
							item
							className={classes.bottomPad}>
							<WorkflowButton
								disabled={isDeleted}
								className={`${classes.button} ${workFlow[status].secondNextLabelClass}`}
								label={workFlow[status].secondNextLabel}
								startIcon={<FontAwesomeIcon className={`${classes.icon} ${workFlow[status].secondNextIconClass}`} icon={workFlow[status].secondNextIcon} />}
								onClick={() => onShareItemClicked(activityId, workFlow[status].secondId, workFlow[status].secondMsg)}
							// onClick={() => moveStatusHandler(activityId, workFlow[status].secondId, workFlow[status].secondMsg)} 
							/>
						</Grid> : null
					}
					{status !== Constants.ACTIVITY.STATUSES.RESOLVED.ID && !isDeleted ? (
						<Grid
							item
							className={classes.bottomPad}>
							<WorkflowButton
								className={classes.button}
								label="No Action Needed"
								startIcon={<FontAwesomeIcon className={classes.icon} icon={["far", "exclamation-circle"]} />}
								onClick={noActionHandler} />
						</Grid>
					) : null
					}
				</Grid>
				<EscalationConfirmationDialog open={escalationConfirmationOpen}
					closeHandler={handleEscalationConfirmationClose}
					submitHandler={handleEscalationConfirmationSubmit} />
			</React.Fragment>
		</ReactPlaceholder>
	);
}, (prevProps, newProps) => {
	// if they're the same, don't re-render (return true)
	return JSON.stringify(prevProps) === JSON.stringify(newProps);
});

ActivityWorkflowActions.displayName = "ActivityWorkflowActions";

ActivityWorkflowActions.defaultProps = {
	status: 1,
	careStatus: 0
};

ActivityWorkflowActions.propTypes = {
	status: PropTypes.number,
	careStatus: PropTypes.number,
	sourceName: PropTypes.string,
	isDeleted: PropTypes.bool,
	activityId: PropTypes.number,
	noActionHandler: PropTypes.func.isRequired,
	careStatusHandler: PropTypes.func.isRequired,
	moveStatusHandler: PropTypes.func.isRequired,
	activity: PropTypes.object,
	showSnackbar: PropTypes.func,
	updateActivityHandler: PropTypes.func,
};

ActivityWorkflowActions.whyDidYouRender = Constants.ENV.IS_LOCAL;

export default ActivityWorkflowActions;